'use client';

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { CalendarEvent, CalendarEventTipo } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, Trash2, Loader2, Church, CalendarDays, Cross } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface EventFormProps {
    event?: Partial<CalendarEvent>;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: Omit<CalendarEvent, 'id'> | CalendarEvent) => Promise<any>;
    onDelete?: (eventId: string) => void;
}

export function EventForm({ event, isOpen, onClose, onSubmit, onDelete }: EventFormProps) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [start, setStart] = useState<Date | undefined>(undefined);
    const [end, setEnd] = useState<Date | undefined>(undefined);
    const [allDay, setAllDay] = useState(false);
    const [color, setColor] = useState('#b4841c'); // default primary color
    const [tipo, setTipo] = useState<CalendarEventTipo>('normal');
    const [hermandadId, setHermandadId] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const { jartinetData } = useAuth();
    const hermandades = useMemo(
        () => [...(jartinetData?.hermandades || [])].sort((a, b) => a.nombre.localeCompare(b.nombre)),
        [jartinetData?.hermandades],
    );

    useEffect(() => {
        if (isOpen && event) {
            setTitle(event.title || '');
            setDescription(event.description || '');
            setStart(event.start ? new Date(event.start) : undefined);
            setEnd(event.end ? new Date(event.end) : undefined);
            setAllDay(event.allDay || false);
            setColor(event.color || '#b4841c');
            setTipo(event.tipo === 'procesion' || event.tipo === 'cultos' ? event.tipo : 'normal');
            setHermandadId(event.hermandadId || '');
        } else {
            // Reset form for new event
            setTitle('');
            setDescription('');
            setStart(new Date());
            setEnd(new Date());
            setAllDay(false);
            setColor('#b4841c');
            setTipo('normal');
            setHermandadId('');
        }
    }, [event, isOpen]);

    const handleStartTimeChange = (time: string) => {
        if (!start) return;
        const [hours, minutes] = time.split(':').map(Number);
        const newStart = new Date(start);
        newStart.setHours(hours, minutes);
        setStart(newStart);
        // If end is before new start, update end as well
        if (end && newStart > end) {
            setEnd(newStart);
        }
    };

    const handleEndTimeChange = (time: string) => {
        if (!end) return;
        const [hours, minutes] = time.split(':').map(Number);
        const newEnd = new Date(end);
        newEnd.setHours(hours, minutes);
        setEnd(newEnd);
    };

    const handleSubmit = async () => {
        if (!start || !end) return;
        setIsLoading(true);
        try {
            const eventData = {
                ...(event?.id && { id: event.id }),
                title,
                description,
                start,
                end,
                allDay,
                color,
                tipo, // siempre string: Firestore cliente rechaza undefined
                hermandadId, // '' = sin hermandad (nunca undefined)
            };
            await onSubmit(eventData);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleDelete = async () => {
       if (event?.id && onDelete) {
            setIsLoading(true);
            try {
                await onDelete(event.id!);
            } finally {
                setIsLoading(false);
            }
       }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{event?.id ? 'Editar Evento' : 'Crear Nuevo Evento'}</DialogTitle>
                    <DialogDescription>
                        Rellena los detalles de tu evento. Haz clic en guardar cuando hayas terminado.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="title" className="text-right">Título</Label>
                        <Input id="title" value={title} onChange={e => setTitle(e.target.value)} className="col-span-3" />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label className="text-right">Tipo</Label>
                        <div className="col-span-3 grid grid-cols-3 gap-2">
                            {([
                                ['normal', 'Evento', <CalendarDays className="h-4 w-4" key="n" />, 'border-primary/50 bg-primary/15 text-primary'],
                                ['procesion', 'Procesión', <Church className="h-4 w-4" key="p" />, 'border-amber-400/60 bg-amber-400/15 text-amber-300'],
                                ['cultos', 'Cultos', <Cross className="h-4 w-4" key="c" />, 'border-violet-400/60 bg-violet-400/15 text-violet-300'],
                            ] as const).map(([value, label, icon, activeCls]) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setTipo(value as CalendarEventTipo)}
                                    className={cn(
                                        'flex flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs font-medium transition-colors',
                                        tipo === value ? activeCls : 'border-white/10 text-muted-foreground hover:text-foreground',
                                    )}
                                >
                                    {icon} {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="hermandadId" className="text-right">Hermandad</Label>
                        <select
                            id="hermandadId"
                            value={hermandadId}
                            onChange={e => setHermandadId(e.target.value)}
                            className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                            <option value="">Sin hermandad</option>
                            {hermandades.map(h => (
                                <option key={h.id} value={h.id}>{h.nombre}</option>
                            ))}
                        </select>
                    </div>
                    {hermandadId && (
                        <p className="col-span-4 -mt-2 text-right text-xs text-muted-foreground">
                            El evento usará el color y el escudo de la hermandad.
                        </p>
                    )}
                    <div className="grid grid-cols-4 items-start gap-4">
                        <Label htmlFor="description" className="text-right pt-2">Descripción</Label>
                        <RichTextarea id="description" value={description} onChange={e => setDescription(e.target.value)} className="col-span-3" />
                    </div>
                     <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="allDay" className="text-right">Todo el día</Label>
                        <Checkbox id="allDay" checked={allDay} onCheckedChange={(checked) => setAllDay(Boolean(checked))} />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label className="text-right">Inicio</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="col-span-3 justify-start font-normal">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {start ? format(start, "PPP", { locale: es }) : <span>Elige una fecha</span>}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar mode="single" selected={start} onSelect={setStart} initialFocus />
                            </PopoverContent>
                        </Popover>
                    </div>
                    {!allDay && (
                         <div className="grid grid-cols-4 items-center gap-4">
                             <Label className="text-right"></Label>
                             <Input type="time" value={start ? format(start, 'HH:mm') : ''} onChange={e => handleStartTimeChange(e.target.value)} className="col-span-3" />
                         </div>
                    )}
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label className="text-right">Fin</Label>
                         <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="col-span-3 justify-start font-normal">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {end ? format(end, "PPP", { locale: es }) : <span>Elige una fecha</span>}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar mode="single" selected={end} onSelect={setEnd} initialFocus />
                            </PopoverContent>
                        </Popover>
                    </div>
                    {!allDay && (
                         <div className="grid grid-cols-4 items-center gap-4">
                             <Label className="text-right"></Label>
                             <Input type="time" value={end ? format(end, 'HH:mm') : ''} onChange={e => handleEndTimeChange(e.target.value)} className="col-span-3" />
                         </div>
                    )}
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="color" className="text-right">Color</Label>
                        <div className="col-span-3 flex items-center gap-2">
                            <Input
                                id="color" type="color"
                                // El selector nativo solo acepta #rrggbb completo; mientras
                                // se teclea el hex a mano puede estar a medias.
                                value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#b4841c'}
                                onChange={e => setColor(e.target.value)}
                                className="h-10 w-14 shrink-0 p-1"
                            />
                            {/* Código hexadecimal, editable a mano */}
                            <Input
                                aria-label="Código hexadecimal del color"
                                value={color.toUpperCase()}
                                onChange={e => {
                                    const v = e.target.value.trim();
                                    const withHash = v.startsWith('#') ? v : `#${v}`;
                                    setColor(withHash.slice(0, 7).toLowerCase());
                                }}
                                placeholder="#B4841C"
                                maxLength={7}
                                className="font-mono uppercase"
                            />
                        </div>
                    </div>
                </div>
                <DialogFooter className="flex justify-between w-full">
                   <div>
                        {event?.id && onDelete && (
                           <AlertDialog>
                               <AlertDialogTrigger asChild>
                                    <Button variant="destructive" size="icon" disabled={isLoading}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                               </AlertDialogTrigger>
                               <AlertDialogContent>
                                   <AlertDialogHeader>
                                       <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
                                       <AlertDialogDescription>Esta acción no se puede deshacer. El evento será eliminado permanentemente.</AlertDialogDescription>
                                   </AlertDialogHeader>
                                   <AlertDialogFooter>
                                       <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                       <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                                   </AlertDialogFooter>
                               </AlertDialogContent>
                           </AlertDialog>
                        )}
                   </div>
                   <div className="flex gap-2">
                     <DialogClose asChild>
                        <Button variant="outline">Cancelar</Button>
                     </DialogClose>
                     <Button onClick={handleSubmit} disabled={isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar
                     </Button>
                   </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
